import React, { useEffect, useState } from "react";
import { fetchTopics } from "./eng-service";
import { Topic } from "./EnglishModel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Type guards for dynamic example shape detection
function isSynAntExample(ex: any): ex is { word: string; synonym: string; antonym: string } {
  return typeof ex.word === "string" && typeof ex.synonym === "string" && typeof ex.antonym === "string";
}
function isSubstitutionExample(ex: any): ex is { phrase: string; substitution: string } {
  return typeof ex.phrase === "string" && typeof ex.substitution === "string";
}
function isTenseExample(ex: any): ex is { tense: string; example: string } {
  return typeof ex.tense === "string" && typeof ex.example === "string";
}
function isCorrectIncorrectExample(ex: any): ex is { correct: string; incorrect: string } {
  return typeof ex.correct === "string" && typeof ex.incorrect === "string";
}

const EnglishGrid: React.FC = () => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [filters, setFilters] = useState({ name: "" });
  const [page, setPage] = useState(1);
  const [limit] = useState(2);
  const [totalPages, setTotalPages] = useState(1);

  const fetchData = async () => {
    const result = await fetchTopics({ ...filters, page, limit });
    setTopics(result.data);
    setTotalPages(result.totalPages);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  return (
    <div className="p-6">
      {/* 🔍 Search filter */}
      <div className="flex gap-2 mb-6">
        <Input
          placeholder="Search topic..."
          value={filters.name}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, name: e.target.value }))
          }
        />
        <Button onClick={() => setPage(1)}>Search</Button>
      </div>

      {/* 🃏 Grid of Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        {topics.map((topic: Topic) => (
          <Card key={topic.id} className="shadow-lg rounded-2xl">
  <CardContent className="p-4">
    <h2 className="text-xl font-bold mb-2">{topic.name}</h2>
    <p className="text-muted-foreground mb-3">{topic.description}</p>

    {/* Examples */}
    <div className="mb-3 p-3 rounded-lg bg-accent text-foreground">
      <h3 className="font-semibold">Examples:</h3>
      <ul className="list-disc list-inside text-sm space-y-2">
        {(topic.examples ?? []).map((ex: any, idx: number) => (
          <li key={idx}>
            {isSynAntExample(ex) && (
              <>
                <b>Word:</b> {ex.word} <br />
                <span>
                  <b className="ml-4">1. Synonym:</b> {ex.synonym} <br />
                  <b className="ml-4">2. Antonym:</b> {ex.antonym}
                </span>
              </>
            )}
            {isSubstitutionExample(ex) && (
              <>
                <b>Phrase:</b> {ex.phrase} <br />
                <b>Substitution:</b> {ex.substitution}
              </>
            )}
            {isTenseExample(ex) && (
              <>
                <b>{ex.tense}:</b> {ex.example}
              </>
            )}
            {isCorrectIncorrectExample(ex) && (
              <>
                ✅ <b>Correct:</b> {ex.correct} <br />❌ <b>Incorrect:</b>{" "}
                {ex.incorrect}
              </>
            )}
            {!isSynAntExample(ex) &&
              !isSubstitutionExample(ex) &&
              !isTenseExample(ex) &&
              !isCorrectIncorrectExample(ex) && (
                <pre className="text-xs">{JSON.stringify(ex, null, 2)}</pre>
              )}
          </li>
        ))}
      </ul>
    </div>

    {/* Exercise */}
    <div className="p-3 rounded-lg bg-muted text-foreground">
      <h3 className="font-semibold">Exercise:</h3>
      {(topic.exercises ?? []).map((ex, idx) => (
        <div key={idx} className="mb-3">
          <p className="text-sm">{ex.question}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {ex.options.map((opt, i) => (
              <Button
                key={i}
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={() =>
                  alert(opt === ex.answer ? "✅ Correct!" : "❌ Try again")
                }
              >
                {opt}
              </Button>
            ))}
          </div>
        </div>
      ))}
    </div>
  </CardContent>
</Card>

        ))}
      </div>

      {/* 📄 Pagination */}
      <div className="flex justify-center gap-3 mt-6">
        <Button
          disabled={page === 1}
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
        >
          Previous
        </Button>
        <span className="px-2 text-sm">
          Page {page} of {totalPages}
        </span>
        <Button
          disabled={page === totalPages}
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default EnglishGrid;